import { XHRInstance, IListResponse, useXHR } from "@sembark-travel/xhr"
import { IUser, useAuthUser } from "../Auth"
import { Required } from "utility-types"
import { TFormattedMoney } from "@sembark-travel/money"
import { IGivenQuote } from "../Trips/store"
import { IInstalment } from "../Payments"
import useSWR, { SWRConfiguration } from "swr"
import type { TSearchParams } from "@sembark-travel/ui-list"
import { IHotel } from "../Hotels"
import { IAccount } from "../Accounting"
import {
	TAutomationSourceVariable,
	TAutomationTemplate,
	TAutomationTemplateDataSource,
} from "../Automations/store"
import type {
	IWhatsAppTemplateEditorValue,
	TWhatsAppTemplateSampleMedia,
	TWhatsAppTemplateLanguageOption,
} from "../WhatsAppMessages"
import { TTeam } from "../Teams"

export type TToken = {
	id: number
	name: string
	abilities: Array<string>
	last_used_at: string | null
	created_at: string
	updated_at: string | null
}

export type TIntegration = {
	id: number
	uid: string
	name: string
	description: string
	abilities: Array<string>
	created_at: string
	created_by?: IUser
	platform?:
		| "google_ads"
		| "meta"
		| "api"
		| "email_mailbox"
		| "pg_payu"
		| "pg_simulator"
		| "whatsapp_cloud_api"
		| "whatsapp_simulator"
		| "tc_knowlarity"
		| "tc_tatatele"
		| "tc_callerdesk"
		| "oauth"
	tokens?: Array<TToken> | null
	integrated_app_type:
		| null
		| "fb_integrated_apps"
		| "mail_integrated_apps"
		| "pg_integrated_apps"
		| "wa_integrated_apps"
		| "telecalling_integrated_apps"
		| "oauth_integrated_apps"
	integrated_app_id: null | number
	/** Present on the detail endpoint of an OAuth app integration. */
	oauth_app?: TOAuthIntegratedApp
	/** On every OAuth app row, including lists: which client it was made for. */
	oauth_client_type?: TOAuthClientType
	in_test_mode_at?: string
	paused_at?: string
	paused_by?: IUser
	google_ads_lead_form_webhook_config?: {
		url: string
		key: string
	}
	telecalling_webhook_config?: {
		key: string
		url: string
	}
}

export type TNewIntegrationSchema = {
	in_test_mode?: boolean | 0 | 1 | "0" | "1"
	name: string
	description: string
	abilities: Array<string>
	platform: Required<TIntegration>["platform"]
}

/**
 * An OAuth app: Sembark is the authorization server, the external client
 * (Claude, ChatGPT or any OAuth 2.1 client) holds the client id and secret
 * we issued and acts on behalf of a user who approved it. The preset only
 * pre-fills the redirect URI the vendor publishes and picks the setup
 * instructions.
 */
export type TOAuthClientType = "claude" | "chatgpt" | "custom"

export type TOAuthScope = {
	id: string
	description: string
	group?: string
}

/**
 * The authorization server's endpoints, plus the resources an app's tokens
 * are accepted by (the MCP server today).
 */
export type TOAuthEndpoints = {
	authorization_url: string
	token_url: string
	authorization_server_metadata_url: string
	mcp_url: string
}

export type TOAuthConnectedUser = {
	user: IUser
	scopes: Array<string>
	authorized_at: string | null
	expires_at: string | null
	last_used_at: string | null
	requests_last_30d: number
}

/**
 * An OAuth app as the signed-in member sees it in their own settings: what
 * they approved, not how the app is configured.
 */
export type TConnectedOAuthApp = {
	uid: string
	name: string
	client_type: TOAuthClientType
	client_type_name: string
	is_paused: boolean
	scopes: Array<TOAuthScope>
	authorized_at: string | null
	expires_at: string | null
	last_used_at: string | null
}

export type TOAuthAccessLogOutcome = "ok" | "error" | "denied" | "invalid"

/**
 * One request an OAuth app made on a member's behalf, on whichever
 * token-protected `surface` it called. Response bodies are never stored, so
 * none are shown.
 */
export type TOAuthAccessLog = {
	id: number
	user?: IUser | null
	surface: string
	method: string | null
	operation: string | null
	arguments: Record<string, unknown> | null
	resource_ids: Record<string, Array<number | string>> | null
	outcome: TOAuthAccessLogOutcome
	error: string | null
	status: number
	duration_ms: number | null
	ip: string | null
	created_at: string
}

export type TOAuthAccessLogFilters = TSearchParams & {
	user?: number
	surface?: string
	operation?: string
	outcome?: TOAuthAccessLogOutcome
	from?: string
	to?: string
}

export type TOAuthIntegratedApp = {
	id: number
	uid: string
	client_id: string
	client_type: TOAuthClientType
	client_type_name: string
	redirect_uris: Array<string>
	allowed_scopes: Array<TOAuthScope>
	endpoints: TOAuthEndpoints
	connected_users: Array<TOAuthConnectedUser>
	updated_at?: string
}

/** Returned once, right after creation or a secret rotation. */
export type TOAuthClientCredentials = {
	client_id: string
	client_secret: string
}

export type TOAuthClientTypeOption = {
	id: TOAuthClientType
	name: string
	redirect_uris: Array<string>
}

export type TOAuthIntegrationOptions = {
	client_types: Array<TOAuthClientTypeOption>
	scopes: Array<TOAuthScope>
	endpoints: TOAuthEndpoints
}

export type TNewOAuthIntegrationSchema = TNewIntegrationSchema & {
	platform: "oauth"
	oauth: {
		/** Empty until the admin picks one; the form requires a choice. */
		client_type: TOAuthClientType | ""
		redirect_uris: Array<string>
		allowed_scopes: Array<string>
	}
}

export type TOAuthIntegration = Required<
	Omit<TIntegration, "integrated_app_type" | "oauth_app">,
	"integrated_app_id"
> & {
	integrated_app_type: "oauth_integrated_apps"
	oauth_app: TOAuthIntegratedApp
}

export function isOAuthIntegration(
	integration: TIntegration
): integration is TOAuthIntegration {
	return Boolean(
		integration.integrated_app_id &&
		integration.integrated_app_type === "oauth_integrated_apps" &&
		integration.platform === "oauth" &&
		integration.oauth_app
	)
}

export type TPaymentMethod = {
	id: string
	name: string
}

export type TPGSubAccount = {
	id: number
	pg_integrated_app_id: number
	pg_provider_reference_id: string
	display_name?: string | null
	email?: string | null
	is_verified?: boolean
	hotel?: IHotel | null
	created_at?: string
	updated_at?: string
}

export type TPGIntegratedApp = {
	id: number
	uid: string
	integration_uid: string
	payment_methods?: Array<TPaymentMethod>
	updated_at?: string
	bank_accounting_account?: IAccount
	sub_accounts?: Array<TPGSubAccount>
} & (
	| {
			pg_provider: "payu"
			payu: {
				merchant_id: string
			}
	  }
	| {
			pg_provider: "pg_simulator"
	  }
)

export type TFBIntegratedApp = {
	id: number
	app_id: string
	user_id: string
	integration_uid: string
	user_access_token_issued_at?: string
	webhook_token: string
	pages?: Array<TFBIntegratedPage>
}

export type TFBIntegratedPage = {
	id: number
	page_id: string
	name: string
	subscribed_at?: string
	access_token_issued_at?: string
	exception?: Record<string, unknown>
	forms_count?: number
}

export type TFBPageLead = {
	id: number
	leadgen_id: string
	page_id: string
	created_at: string
	webhook_at?: string
	webhook_payload?: Record<string, unknown>
	fetched_data?: Record<string, unknown>
	fetched_at?: string
	batch_id?: string
	form_id?: string
	ad_id?: string
	adgroup_id: string
	exception?: Record<string, unknown>
}

export type TFBBusinessAccount = {
	id: number
	fb_business_account_id: number | string
	name: string
	verification_status: "verified" | "not_verified" | "pending" | string
	created_at: string
}

export type TWhatsappBusinessAccount = {
	id: number
	name: string
	fb_business_account_id: number
	fb_business_account?: TFBBusinessAccount
	wa_phone_numbers: Array<TWhatsappBusinessAccountPhoneNumber>
	/** Templates under this account; the Templates tab lists them. */
	wa_message_templates_count?: number
	created_at: string
	updated_at?: string
	account_review_status: "APPROVED" | "PENDING" | "REJECTED" | "RESTRICTED"
	wa_business_account_id: string
}

/**
 * One bucket of Meta's `pricing_analytics`: what was sent in a period for a
 * pricing category and type, to a country, from a phone number.
 */
export type TWAPricingDataPoint = {
	/** Unix seconds */
	start: number
	/** Unix seconds */
	end: number
	country?: string
	phone_number?: string
	pricing_category?: string
	pricing_type?: string
	volume?: number
	/** In the account's currency */
	cost?: number
}

/** Read live from Meta; nothing of it is stored. */
export type TWAPricingAnalytics = {
	start: string
	end: string
	granularity: "DAILY" | "MONTHLY"
	currency: string | null
	data_points: Array<TWAPricingDataPoint>
}

export type TWhatsappBusinessAccountPhoneNumber = {
	id: number
	name: string
	wa_business_account_id: number
	wa_phone_number_id: number
	status:
		| "PENDING"
		| "LINKED"
		| "UNLINKED"
		| "DELETED"
		| "MIGRATED"
		| "BANNED"
		| "RESTRICTED"
		| "CONNECTED"
	quality_rating: "GREEN" | "YELLOW" | "RED" | "UNKNOWN"
	display_phone_number: string
	/** LIVE once the number is registered for production; SANDBOX before. */
	account_mode?: "LIVE" | "SANDBOX" | string
	created_at: string
	updated_at?: string
	is_official_business_account: 0 | 1
	/**
	 * Meta's welcome messages setting, as last synced. While on, a customer
	 * who opens the chat from an ad or a link without typing still reaches
	 * us, and can be greeted.
	 */
	welcome_messages_enabled?: boolean
	/**
	 * The two-step verification PIN Sembark set when it registered the
	 * number. WhatsApp Manager asks for it before deleting a connected
	 * number, so it stays findable here.
	 */
	two_step_pin?: string | null
	/**
	 * The number is also used in the WhatsApp Business app (coexistence).
	 * Messages sent from the app show in Sembark.
	 */
	is_on_biz_app?: boolean
	platform_type?: string | null
	/** Progress of the Business app contacts and chat history sync. */
	business_app_sync?: {
		contacts_requested_at?: string
		history_requested_at?: string
		history_progress?: number
		history_declined?: boolean
		disconnected_reason?: string | null
	} | null
	messaging_limit_tier:
		| "TIER_50"
		| "TIER_250"
		| "TIER_1K"
		| "TIER_10K"
		| "TIER_100K"
		| "TIER_UNLIMITED"
}

export type TWAIntegratedApp = {
	id: number
	integration_uid: string
	integration: TIntegration
	wa_business_account: TWhatsappBusinessAccount
	wa_business_accounts?: TWhatsappBusinessAccount[]
	wa_phone_number: TWhatsappBusinessAccountPhoneNumber
	provider: "whatsapp_cloud" | "whatsapp_simulator"
	/**
	 * How the current authorisation was obtained. Decides which of the two
	 * flows a reconnect should use. Null until connected.
	 */
	connection_method?: "embedded_signup" | "facebook_login" | null
	/**
	 * What the Embedded Signup dialog reported: the business, account and
	 * number it allocated. Null for a Facebook Login connection.
	 */
	embedded_signup?: {
		business_id?: string
		waba_id?: string
		phone_number_id?: string
	} | null
	/** Facebook user id behind the authorisation. */
	user_id?: string | null
	/** Permissions the authorisation granted. */
	scopes?: Array<string> | null
	default_team_id?: number | null
	default_team?: { id: number; name: string } | null
	/**
	 * Where every outbound message goes while the integration is in test
	 * mode (`integration.in_test_mode_at`). Kept when test mode is off.
	 */
	test_recipient?: string | null
	user_access_token_issued_at?: string
	user_access_token_expires_at?: string
	created_at: string
	updated_at?: string
	created_by?: IUser
}

export type TWAWebhookHealthStatus = "ok" | "warning" | "error"

/**
 * One business account's row inside the `business_accounts` check details.
 */
export type TWAWebhookHealthAccount = {
	id: number
	name: string | null
	wa_business_account_id: string
	status: TWAWebhookHealthStatus
	message: string
}

export type TWAWebhookHealthCheck = {
	key: string
	label: string
	status: TWAWebhookHealthStatus
	message: string
	details?: unknown
}

export type TWAWebhookHealth = {
	status: TWAWebhookHealthStatus
	checked_at: string
	checks: Array<TWAWebhookHealthCheck>
}

export type TWATemplate = {
	id: number
	automation_template_key?: string | null
	automation_template?: { key: string; name: string } | null
	/**
	 * The source this template's variables are filled from, and the variables
	 * themselves. A template naming no variable fits any automation; one that
	 * does only fits automations running on this source.
	 */
	template_data_source_key?: string | null
	required_parameter_names?: Array<string>
	/** Resolved from `template_data_source_key`. */
	template_data_source_name?: string | null
	wa_business_account: TWhatsappBusinessAccount
	wa_business_account_id: number
	wa_phone_number_id?: number | null
	phone_number?: TWhatsappBusinessAccountPhoneNumber | null
	wa_template_id: number
	name: string
	header?: string | null
	body?: string | null
	preview?: {
		header_type?: string | null
		header?: string | null
		header_image_url?: string | null
		header_image_mode?: string | null
		header_dynamic_source_key?: string | null
		header_registration_handle?: string | null
		header_media_url?: string | null
		header_media_mode?: string | null
		header_sample_media?: TWhatsAppTemplateSampleMedia | null
		body?: string | null
		footer?: string | null
		buttons?: Array<{
			type?: string | null
			otp_type?: string | null
			text?: string | null
			url?: string | null
			phone_number?: string | null
		}>
	}
	language: string
	message_send_ttl_seconds?: number | null
	status: "APPROVED" | "PENDING" | "REJECTED"
	category: "MARKETING" | "UTILITY" | "AUTHENTICATION"
	parameter_format?: "NAMED" | "POSITIONAL" | null
	components?: Array<{
		type: string
		format?: string
		text?: string
		buttons?: Array<Record<string, unknown>>
		limited_time_offer?: {
			text?: string
			has_expiration?: boolean
		}
		example?: {
			body_text_named_params?: Array<{
				param_name: string
				example: string
			}>
			header_text_named_params?: Array<{
				param_name: string
				example: string
			}>
			footer_text_named_params?: Array<{
				param_name: string
				example: string
			}>
			body_text?: Array<Array<string>>
			header_text?: Array<string>
			footer_text?: Array<string>
			header_handle?: Array<string> | string
		}
	}>
	created_at: string
	updated_at?: string
}

/**
 * Why a template cannot be sent by an automation running on `sourceKey`, or
 * null when it can. Mirrors the rule the automation save enforces: a template
 * naming no variable fits anywhere, and one that does only fits the source its
 * variables are filled from.
 */
export function waTemplateSourceMismatch(
	template: Pick<
		TWATemplate,
		| "template_data_source_key"
		| "template_data_source_name"
		| "required_parameter_names"
	>,
	sourceKey?: string | null
): string | null {
	if (!sourceKey || !template.required_parameter_names?.length) {
		return null
	}

	if (template.template_data_source_key === sourceKey) {
		return null
	}

	return template.template_data_source_name
		? `Fills its variables from ${template.template_data_source_name}`
		: "Its variables can't be filled by this automation"
}

export type TWAEditorTemplateResponse = {
	template: TWATemplate
	editor_supported: boolean
	editor_unsupported_reason?: string | null
	editor?: IWhatsAppTemplateEditorValue | null
	available_languages: Array<TWhatsAppTemplateLanguageOption>
	automation_template?: TWhatsAppAutomationTemplate | null
}

export type TWAEditorBootstrapResponse = {
	editor: IWhatsAppTemplateEditorValue
	available_languages: Array<TWhatsAppTemplateLanguageOption>
}

export type TTemplateVariable = TAutomationSourceVariable

/**
 * One of a template's variables as a particular record fills it: the
 * variable itself, the value it came out to, and whether that value is
 * there at all.
 */
export type TTemplateDataItem = TTemplateVariable & {
	aliases?: string[]
	value: string
	resolved: boolean
}

export type TTemplateDataSource = TAutomationTemplateDataSource

export type TWhatsAppAutomationTemplate = TAutomationTemplate

export type TWhatsAppIntegration = Required<
	Omit<TIntegration, "integrated_app_type">,
	"integrated_app_id"
> & { integrated_app_type: "wa_integrated_apps" }

export type TMailIntegration = Required<
	Omit<TIntegration, "integrated_app_type">,
	"integrated_app_id"
> & { integrated_app_type: "mail_integrated_apps" }

export type TIntegratedMailbox = {
	id: number
	mail_integrated_app_id: number
	provider: "imap_smtp" | "gmail" | "outlook"
	name: string
	email: string
	is_active: boolean
	inbound_enabled: boolean
	outbound_enabled: boolean
	push_read_flag_to_imap?: boolean
	allow_all_to_read?: boolean
	access_teams?: Array<{
		team: TTeam
		can_read?: boolean | null
		can_send_templates?: boolean | null
		can_send_replies?: boolean | null
	}>
	credentials?: {
		imap?: {
			host?: string | null
			port?: number | null
			encryption?: "ssl" | "tls" | "none" | null
			username?: string | null
			password?: string | null
		} | null
		smtp?: {
			host?: string | null
			port?: number | null
			encryption?: "ssl" | "tls" | "none" | null
			username?: string | null
			password?: string | null
		} | null
	}
	settings?: Record<string, unknown> | null
	last_connection_checked_at?: string | null
	last_connection_status?: string | null
	last_connection_error?: string | null
	last_connection_details?: Record<string, unknown> | null
	sync_interval_minutes?: number | null
	sync_status?: "queued" | "syncing" | "success" | "failed" | null
	sync_error?: string | null
	last_sync_queued_at?: string | null
	last_sync_started_at?: string | null
	last_synced_at?: string | null
	created_at: string
	updated_at?: string
}

export type TMailIntegratedApp = {
	id: number
	integration_uid: string
	settings?: Record<string, unknown> | null
	integration?: TIntegration
	mailboxes?: Array<TIntegratedMailbox>
	gmail_auth_url?: string | null
	// Per-tenant Gmail OAuth client. The secret is never returned.
	gmail_oauth_configured?: boolean
	gmail_oauth_client_id?: string | null
	gmail_callback_url?: string | null
	// Whether to expose the bring-your-own OAuth client UI (global client
	// unverified, or tenant already configured a custom client).
	gmail_oauth_customizable?: boolean
	// Outlook mirror of the gmail_* block. tenant_id is the Entra
	// directory id for single-tenant BYO app registrations.
	outlook_auth_url?: string | null
	outlook_oauth_configured?: boolean
	outlook_oauth_client_id?: string | null
	outlook_oauth_tenant_id?: string | null
	outlook_callback_url?: string | null
	outlook_oauth_customizable?: boolean
	created_at: string
	updated_at?: string
}

export function isWhatsAppCloudApiIntegration(
	integration: TIntegration
): integration is TWhatsAppIntegration {
	return Boolean(
		integration.integrated_app_id &&
		integration.integrated_app_type === "wa_integrated_apps" &&
		(integration.platform === "whatsapp_cloud_api" ||
			integration.platform === "whatsapp_simulator")
	)
}

export function isMailboxIntegration(
	integration: TIntegration
): integration is TMailIntegration {
	return Boolean(
		integration.integrated_app_id &&
		integration.integrated_app_type === "mail_integrated_apps" &&
		integration.platform === "email_mailbox"
	)
}

// actions
export function XHR(xhr: XHRInstance) {
	return {
		async store(data: TNewIntegrationSchema): Promise<{
			data: TIntegration
			meta?: {
				last_created_token?: string
				oauth_client?: TOAuthClientCredentials
			}
		}> {
			return xhr.post("/integrations", data).then((resp) => resp.data)
		},
		async createOptions(): Promise<{ oauth: TOAuthIntegrationOptions }> {
			return xhr.get("/integrations/create").then((resp) => resp.data.data)
		},
		async regenerateOAuthSecret(
			integrationId: number
		): Promise<TOAuthClientCredentials> {
			return xhr
				.post(`/integrations/${integrationId}/oauth/secret`)
				.then((resp) => resp.data.data)
		},
		async updateOAuthApp(
			integrationId: number,
			payload: {
				allowed_scopes?: Array<string>
				redirect_uris?: Array<string>
			}
		): Promise<TIntegration> {
			return xhr
				.patch(`/integrations/${integrationId}/oauth`, payload)
				.then((resp) => resp.data.data)
		},
		async disconnectOAuthMember(
			integrationId: number,
			userId: number
		): Promise<void> {
			await xhr.delete(`/integrations/${integrationId}/oauth/users/${userId}`)
		},
		async getConnectedOAuthApps(): Promise<Array<TConnectedOAuthApp>> {
			return xhr.get("/oauth/connected-apps").then((resp) => resp.data.data)
		},
		async disconnectConnectedOAuthApp(appUid: string): Promise<void> {
			await xhr.delete(`/oauth/connected-apps/${appUid}`)
		},
		async getOAuthRequests(
			integrationId: number,
			params: TOAuthAccessLogFilters
		): Promise<IListResponse<TOAuthAccessLog>> {
			return xhr
				.get(`/integrations/${integrationId}/oauth/requests`, { params })
				.then((resp) => resp.data)
		},
		async get(params: unknown): Promise<IListResponse<TIntegration>> {
			return xhr.get("/integrations", { params }).then((resp) => resp.data)
		},
		async show(integrationId: string | number): Promise<TIntegration> {
			return xhr
				.get(`/integrations/${integrationId}`)
				.then((resp) => resp.data.data)
		},
		async update(
			integrationId: string | number,
			payload: unknown
		): Promise<TIntegration> {
			return xhr
				.patch(`/integrations/${integrationId}`, payload)
				.then((resp) => resp.data.data)
		},
		async destroy(id: number): Promise<string | undefined> {
			return xhr
				.delete(`/integrations/${id}`)
				.then((resp) => resp.data?.data?.message)
		},
		async pause(id: number): Promise<string | undefined> {
			return xhr
				.post(`/integrations/${id}/pause`)
				.then((resp) => resp.data?.data?.message)
		},
		async resume(id: number): Promise<string | undefined> {
			return xhr
				.post(`/integrations/${id}/resume`)
				.then((resp) => resp.data?.data?.message)
		},
		async generateToken(id: number): Promise<{ token: string }> {
			return xhr
				.post(`/integrations/${id}/tokens`)
				.then((resp) => resp.data.data)
		},
		async destroyToken(
			integrationId: number,
			tokenId: number
		): Promise<string | undefined> {
			return xhr
				.delete(`/integrations/${integrationId}/tokens/${tokenId}`)
				.then((resp) => resp.data?.data?.message)
		},
	}
}

export type TPaymentGatewayIntegration = Required<
	Omit<TIntegration, "integrated_app_type">,
	"integrated_app_id"
> & { integrated_app_type: "pg_integrated_apps" }

export function isPaymentGatewayIntegration(
	integration: TIntegration
): integration is TPaymentGatewayIntegration {
	return Boolean(
		integration.integrated_app_id &&
		integration.integrated_app_type === "pg_integrated_apps"
	)
}

export type TPGInvoice = {
	id: number
	uid: string
	/** What the gateway charges, in the `currency` below (the converted amount when currency conversion was applied). */
	amount: TFormattedMoney
	/** Currency code of amount (e.g. "INR"). */
	currency: string
	/** Original amount before conversion (e.g. 100 USD). Use this for display. */
	original_amount: TFormattedMoney
	/** Currency code of original_amount (e.g. "USD"). Same as currency when no conversion occurred. */
	original_currency: string
	link: string
	description: string
	is_active: boolean
	paid_at?: string
	status: string
	expiry_date: string
	created_at: string
	deleted_at?: string
	raw_data?: Record<string, unknown>
	payment_link?: TPGPaymentLink
	invoiceable_id: number
	successful_transactions?: Array<TPGTransaction>
	failed_transactions_count?: number
	unverified_successful_transactions_count?: number
	unverified_successful_transactions?: Array<TPGTransaction>
} & (
	| {
			invoiceable_type: "given_quotes"
			invoiceable: Required<IGivenQuote, "trip_quote">
	  }
	| {
			invoiceable_type: "instalments"
			invoiceable: Required<IInstalment, "payment">
	  }
)

export type TPGPaymentLink = {
	id: number
	uid: string
	amount: TFormattedMoney
	link: string
	description: string
	is_active: boolean
	status: string
	expiry_date: string
	created_at: string
	deleted_at?: string
	raw_data?: Record<string, unknown>
	invoice?: TPGInvoice
}

export type TPGTransaction = {
	id: number
	pg_provider_reference_id: string
	status: "success" | "failure"
	currency: string
	amount: TFormattedMoney
	rrn?: string
	arn?: string
	created_at: string
	date: string
	pg_failure?: {
		failure_category: string
		pg_provider_error_message: string | null
	}
	invoice?: TPGInvoice
	settlements?: Array<TPGSettlement>
	settlement_entries?: Array<TPGSettlementEntry>
	setl_txn_amount?: TFormattedMoney
	setl_fee?: TFormattedMoney
	setl_tax?: TFormattedMoney
	setl_date?: string
	setl_fee_inc_tax?: TFormattedMoney
	setl_credit_amount?: TFormattedMoney
	setl_debit_amount?: TFormattedMoney
	method?: string
	fee_and_tax_bifurcation?: TPGChargeBirfucation
	verified_at?: string
}

export type TPGSettlement = {
	id: number
	utr: string
	date: string
	transactions?: Array<TPGTransaction>
	entries?: Array<TPGSettlementEntry>
	request_amount?: TFormattedMoney
	txn_amount?: TFormattedMoney
	tax?: TFormattedMoney
	fee?: TFormattedMoney
	fee_inc_tax?: TFormattedMoney
	credit_amount?: TFormattedMoney
	debit_amount?: TFormattedMoney
	amount?: TFormattedMoney
	created_at: string
	fee_and_tax_bifurcation?: TPGChargeBirfucation
	unlinked_entries_count?: number
}

type TPGChargeBirfucation = {
	// customer is charged with following
	convenience_fee: TFormattedMoney
	convenience_tax: TFormattedMoney
	// merchant is charged with following
	merchant_fee: TFormattedMoney
	merchant_tax: TFormattedMoney
	// total
	total_fee: TFormattedMoney
	total_tax: TFormattedMoney
}

export type TPGSettlementEntry = {
	id: number
	pg_provider_transaction_reference_id: string
	currency: string
	txn_amount: TFormattedMoney
	txn_date: string
	txn_method: string
	fee: TFormattedMoney
	tax: TFormattedMoney
	fee_inc_tax: TFormattedMoney
	credit_amount: TFormattedMoney
	debit_amount: TFormattedMoney
	settlement?: TPGSettlement
	transaction?: TPGTransaction
	fee_and_tax_bifurcation?: TPGChargeBirfucation
}

export function usePaymentGatewayIntegratedAppsCount() {
	const { user } = useAuthUser()
	return user?.tenant?.pg_integrated_apps_count || 0
}

export function useFetchIntegration(id: number | string) {
	const xhr = useXHR()
	return useSWR(`/api/integrations/${id}`, () => XHR(xhr).show(id))
}

/** The OAuth apps the signed-in member is connected to. */
export function useFetchConnectedOAuthApps() {
	const xhr = useXHR()
	return useSWR("/api/oauth/connected-apps", () =>
		XHR(xhr).getConnectedOAuthApps()
	)
}

/** Client-type presets, the scope registry and our endpoints, for the OAuth app forms. */
export function useFetchOAuthIntegrationOptions() {
	const xhr = useXHR()
	return useSWR("/api/integrations/create?oauth", () =>
		XHR(xhr)
			.createOptions()
			.then((data) => data.oauth)
	)
}

export function useFetchPaymentGatewayIntegratedApp(
	id: number | undefined | null | string
) {
	const xhr = useXHR()
	return useSWR(
		id ? `/api/pg/${id}` : null,
		() =>
			xhr
				.get<{
					data: TPGIntegratedApp
					meta: {
						exception?: Record<string, unknown>
						webhook_urls: Array<{
							name: string
							url: string
						}>
					}
				}>(`integrations/pg/${id}`)
				.then((resp) => resp.data),
		{ revalidateOnFocus: true }
	)
}

/**
 * A template rendered for display, with placeholders or sample values -- or,
 * given a source, as that record would send it, along with the variables the
 * record leaves blank. The server checks those by the same rule a send
 * refuses on, so the preview and the check come in one call.
 *
 * The source is the record's kind, which must be the one the template reads
 * its variables from, and its id; the two go together.
 */
export function useFetchWATemplatePreview(
	templateId: number | string,
	renderMode: "raw" | "sample",
	sourceKey?: string,
	sourceRecordId?: number | string
) {
	const xhr = useXHR()
	return useSWR(
		`/api/wa-message-templates/${templateId}/preview/${renderMode}` +
			(sourceKey ? `/${sourceKey}/${sourceRecordId}` : ""),
		() =>
			xhr
				.get<{
					data: {
						rendered_html?: string
						missing_parameter_names?: Array<string>
					}
				}>(`/wa-message-templates/${templateId}`, {
					params: {
						include: "rendered_html",
						render_mode: renderMode,
						source_key: sourceKey,
						source_record_id: sourceRecordId,
					},
				})
				.then((resp) => ({
					html: resp.data.data.rendered_html || "",
					missingParameterNames: resp.data.data.missing_parameter_names || [],
				}))
	)
}

/**
 * A template's variables as one record fills them.
 *
 * Only a template linked to a message type has variables the server can
 * fill, so nothing is asked for any other: a sourceless one is filled in by
 * whoever sends it, and one with no variables has nothing to fill.
 */
export function useFetchWATemplateParameters(
	template:
		| Pick<TWATemplate, "id" | "template_data_source_key">
		| null
		| undefined,
	recordId: number | string
) {
	const xhr = useXHR()
	return useSWR(
		template?.template_data_source_key
			? `/wa-message-templates/${template.id}/parameters/${recordId}`
			: null,
		(url: string) =>
			xhr
				.get<{ data: { items: Array<TTemplateDataItem> } }>(url)
				.then((resp) => resp.data.data.items),
		{ revalidateOnFocus: false }
	)
}

/**
 * Filters on the templates listing. The business account travels in the
 * URL as `<id>_<name>` — the Accounts tab links to the listing that way —
 * so the picker can show it without a fetch.
 */
export interface ITemplateFilters extends TSearchParams {
	q?: string
	page?: number
	waba?: Pick<TWhatsappBusinessAccount, "id" | "name"> | null
}

export interface ITemplateFiltersInLocationQuery extends TSearchParams {
	q?: string
	page?: number
	waba?: string
}

export function templateFiltersToLocationQuery(
	params: ITemplateFilters
): ITemplateFiltersInLocationQuery {
	const { q, page, waba } = params
	const query: ITemplateFiltersInLocationQuery = {}
	if (q) query.q = q
	if (page) query.page = page
	if (waba) {
		query.waba = `${waba.id}_${waba.name}`
	}
	return query
}

export function locationQueryToTemplateFilters(
	query: ITemplateFiltersInLocationQuery
): ITemplateFilters {
	const { q, page, waba } = query
	const filters: ITemplateFilters = {}
	if (q) filters.q = q
	if (page) filters.page = page
	if (waba) {
		const [id, ...name] = waba.split("_")
		if (id && !Number.isNaN(Number(id))) {
			filters.waba = { id: Number(id), name: name.join("_") }
		}
	}
	return filters
}

/**
 * The listing endpoint wants the account's id, and the integration to
 * scope by.
 */
export function templateFiltersToRequestParams(
	filters: ITemplateFilters,
	integratedAppId: number
) {
	const { waba: wa_business_account, page = 1, ...rest } = filters
	return {
		...rest,
		page,
		integration_id: integratedAppId,
		wa_business_account_id: wa_business_account?.id ?? null,
	}
}

/**
 * A WhatsApp Flow as we mirror it. Flows are authored in Meta's Flow
 * Builder; the sync reads them and stores the definition so the screens and
 * the JSON can be shown without asking Meta again. `definition` rides only
 * on the single-Flow endpoint — a Flow's JSON runs to kilobytes.
 */
export type TWAFlowRecord = {
	id: number
	wa_flow_id: string
	name: string
	status: string
	categories: Array<string>
	definition_synced_at: string | null
	definition?: Record<string, unknown> | null
	/** What finishing the Flow sends back; only with the definition. */
	submitted_fields?: Array<TWAFlowSubmittedField>
	wa_business_account?: Pick<TWhatsappBusinessAccount, "id" | "name">
	created_at: string
	updated_at: string
}

/** One key a screen sends when it finishes the Flow. */
export type TWAFlowSubmittedField = {
	screen: string
	key: string
}

/** A ready-made Flow the editor can start from. */
export type TWAFlowPreset = {
	key: string
	name: string
	description: string
	categories: Array<string>
	fields: Array<{
		key: string
		label: string
		/** Always in the Flow and must be answered. */
		required: boolean
		/** Picked when the preset is chosen. */
		default: boolean
	}>
}

/** The categories Meta accepts on a Flow. */
export const WA_FLOW_CATEGORIES = [
	"LEAD_GENERATION",
	"SURVEY",
	"CONTACT_US",
	"CUSTOMER_SUPPORT",
	"APPOINTMENT_BOOKING",
	"SIGN_UP",
	"SIGN_IN",
	"OTHER",
] as const

/**
 * Filters on the Flows listing, mirrored into the URL the same way the
 * templates listing does it.
 */
export interface IFlowFilters extends TSearchParams {
	q?: string
	page?: number
	status?: string
	waba?: Pick<TWhatsappBusinessAccount, "id" | "name"> | null
}

export interface IFlowFiltersInLocationQuery extends TSearchParams {
	q?: string
	page?: number
	status?: string
	waba?: string
}

export function flowFiltersToLocationQuery(
	params: IFlowFilters
): IFlowFiltersInLocationQuery {
	const { q, page, status, waba } = params
	const query: IFlowFiltersInLocationQuery = {}
	if (q) query.q = q
	if (page) query.page = page
	if (status) query.status = status
	if (waba) {
		query.waba = `${waba.id}_${waba.name}`
	}
	return query
}

export function locationQueryToFlowFilters(
	query: IFlowFiltersInLocationQuery
): IFlowFilters {
	const { q, page, status, waba } = query
	const filters: IFlowFilters = {}
	if (q) filters.q = q
	if (page) filters.page = page
	if (status) filters.status = status
	if (waba) {
		const [id, ...name] = waba.split("_")
		if (id && !Number.isNaN(Number(id))) {
			filters.waba = { id: Number(id), name: name.join("_") }
		}
	}
	return filters
}

export function flowFiltersToRequestParams(filters: IFlowFilters) {
	const { waba: wa_business_account, page = 1, ...rest } = filters
	return {
		...rest,
		page,
		wa_business_account_id: wa_business_account?.id ?? null,
	}
}

/**
 * What the WhatsApp integrated-app endpoint sends beside the record: where
 * connecting and reconnecting go, and the last setup failure if any.
 */
export type TWAIntegratedAppMeta = {
	/**
	 * The Embedded Signup page; null until the server has a configuration
	 * for it.
	 */
	embedded_signup_url: string | null
	/** The Facebook Login redirect, kept for a manual steer. */
	legacy_auth_url: string
	legacy_reauth_url: string
	exception?: Record<string, unknown>
}

export function useFetchWAIntegratedApp(
	id: number | null | undefined,
	config?: SWRConfiguration
) {
	const xhr = useXHR()
	return useSWR(
		id ? `/integrations/whatsapp/${id}` : null,
		() =>
			xhr
				.get<{
					data: TWAIntegratedApp
					meta: TWAIntegratedAppMeta
				}>(`/integrations/whatsapp/${id}`)
				.then((resp) => resp.data),
		{ revalidateOnFocus: true, ...config }
	)
}

export function useFetchMailIntegratedApp(
	id: number | undefined | null | string,
	config?: SWRConfiguration
) {
	const xhr = useXHR()
	return useSWR(
		id ? `/api/integrations/mail/${id}` : null,
		() =>
			xhr
				.get<{ data: TMailIntegratedApp }>(`integrations/mail/${id}`)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: true, ...config }
	)
}
